import {
  accessoriesMsg,
  computersMsg,
  networkingHardwareMsg,
  phonesMsg,
  t,
  tabletsMsg,
} from '../../common/i18n/index.js';
import {
  SupportedModelCategories,
  SupportedModelCategory,
  toDisplayableProduct,
} from '../../common/utils/catalogUtils.js';
import { isDefined } from '../../common/utils/objectUtils.js';
import { formatSum, getRecurringPriceAsText } from '../../common/utils/priceUtils.js';
import { filterFn } from '../ProductGrid/ProductGridFilter.js';
import { searchFn } from '../ProductGrid/ProductGridSearch.js';
import { sortFn } from '../ProductGrid/ProductGridSort.js';
import type { CatalogProducts } from '../../common/loaders.js';
import type { CatalogProduct } from '../../common/utils/catalogUtils.js';
import type { Catalog } from '../../generated/api/catalog.js';
import type { OnlineModelHeader } from '../../generated/api/onlineModelHeader.js';
import type { Filters } from '../ProductGrid/ProductGridFilter.js';
import type { SortType } from '../ProductGrid/ProductGridSort.js';

const getDisplayProducts = (
  headers: OnlineModelHeader[],
  catalog: Catalog,
  isPriceLoading?: (onlineModelCode: string) => boolean
) =>
  headers
    .map(model => {
      const product = toDisplayableProduct(model, catalog, false, false);
      return product
        ? {
            ...product,
            isPriceLoading: isPriceLoading?.(product.code) ?? false,
          }
        : null;
    })
    .filter(isDefined);

export const mapLoaderDataToCatalogProducts = (
  loaderData: CatalogProducts,
  catalog: Catalog,
  isPriceLoading?: (onlineModelCode: string) => boolean
) => {
  const products: Record<string, CatalogProduct[]> = {};

  products[SupportedModelCategory.COMPUTERS] = getDisplayProducts(loaderData.computers || [], catalog, isPriceLoading);

  products[SupportedModelCategory.ACCESSORIES] = getDisplayProducts(
    loaderData.accessories || [],
    catalog,
    isPriceLoading
  );

  products[SupportedModelCategory.TABLET] = getDisplayProducts(loaderData.tablets || [], catalog, isPriceLoading);

  products[SupportedModelCategory.PHONE] = getDisplayProducts(loaderData.phones || [], catalog, isPriceLoading);

  products[SupportedModelCategory.NETWORK_EQUIPMENT] = getDisplayProducts(
    loaderData.networkEquipment || [],
    catalog,
    isPriceLoading
  );

  return products;
};

export const getProductCategoryDetails = (category: SupportedModelCategory) => {
  switch (category) {
    case SupportedModelCategory.PHONE:
      return {
        displayText: t.E9IF(phonesMsg),
        showAdditionalInfo: true,
        iconClass: 'phone',
      };
    case SupportedModelCategory.ACCESSORIES:
      return {
        displayText: t.J954(accessoriesMsg),
        showAdditionalInfo: false,
        iconClass: 'headphones',
      };
    case SupportedModelCategory.COMPUTERS:
      return {
        displayText: t.MCW3(computersMsg),
        showAdditionalInfo: false,
        iconClass: 'laptop',
      };
    case SupportedModelCategory.NETWORK_EQUIPMENT:
      return {
        displayText: t.DMM5(networkingHardwareMsg),
        showAdditionalInfo: false,
        iconClass: 'network',
      };
    case SupportedModelCategory.TABLET:
      return {
        displayText: t.XS42(tabletsMsg),
        showAdditionalInfo: false,
        iconClass: 'tablet',
      };
  }
};

// prices can be either oneTime or monthlyRecurring at a time
export const getStartingPrice = (product: CatalogProduct): string =>
  formatSum(product.oneTimeCharge) || `Alk. ${getRecurringPriceAsText(product.monthlyRecurringCharge)}`;

export const sortAndFilterProducts = (
  searchString: string,
  catalogProducts: Record<string, CatalogProduct[]>,
  sortType: SortType,
  selectedFilters: Record<string, Filters>
) => {
  const newProducts: Record<string, CatalogProduct[]> = {};
  SupportedModelCategories.forEach(cat => {
    const filteredItems = catalogProducts[cat]
      .filter(searchFn(searchString))
      .filter(filterFn(selectedFilters[cat]))
      .sort(sortFn(sortType));
    newProducts[cat] = [...filteredItems];
  });
  return newProducts;
};

export const getSelectedCatalogProducts = (
  catalogProducts: Record<string, CatalogProduct[]>,
  selectedProductCodes: string[]
) => {
  const selectedProducts: Record<string, CatalogProduct[]> = {};
  SupportedModelCategories.forEach(cat => {
    const productsForCatalog = catalogProducts[cat]
      .filter(product => selectedProductCodes.includes(product.code))
      .map(i => i);
    if (productsForCatalog.length > 0) {
      selectedProducts[cat] = productsForCatalog;
    }
  });
  return selectedProducts;
};
